/* jshint esversion: 6 */
/* OLD website widget */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

// library imports
import Browser from '../lib/Browser';
import IFrameDomain from '../lib/IFrameDomain';
import LocalSession from '../lib/LocalSession';
import * as UUID from '../lib/uuid';
import { AvoCable } from '../lib/avo-cable';

// React imports
import { WebsiteWidgetPopout, WebsiteWidgetForm } from './subcomponents/website_widget_v2';

const STATUSES = ['qualification', 'chatting', 'thank_you'];

class WebsiteWidget extends React.Component {
  constructor(props) {
    super(props);

    const { wsUrl, widgetId, form } = this.props;
    AvoCable.initWithConfig({ url: wsUrl });

    this.key = `avochato-widget-${widgetId}`;
    this.iframeDomain = IFrameDomain.lookup();
    this.state = {
      sessionId: null,
      status: STATUSES[0],
      authenticityToken: form.authenticityToken,
      open: props.open,
      popoutOpen: false,
      popoutClosed: false,
      initialDomain: this.iframeDomain, // will overwrite if the session has one
    };
    this.websiteWidgetForm = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('message', (e) => this.onWindowMessage(e));
    this.fetchSession();
  }

  fetchSession() {
    if (Browser.isIframe()) {
      // use parent window as source of truth
      this.sendToParentWindow({type: 'fetchSession'});

      // assume the parent page failed to send data back and generate a new state
      setTimeout(() => {
        if (!this.state.sessionId) {
          this.setState(this.getCurrentState(), () => this.regenerateSize());
        }
      }, 500);
      return;
    }

    // otherwise use local window as source of truth
    const sessionData = LocalSession.lookup(this.key) || {};
    sessionData.type = 'fetchSession';
    this.onWindowMessage({data: sessionData});
  }

  onWindowMessage(event) {
    switch(event.data.type) {
      case 'openWithOption':
        this.setState({open: true}, () => this.regenerateSize());
        this.websiteWidgetForm.current.setDropDownValue(event.data.value);
        break;
      case 'fetchSession':
        const currentSession = event.data;
        if (!currentSession.state) {
          // no state saved yet, save yourself!
          return this.setState(this.getCurrentState(), () => this.regenerateSize());
        }

        // always try to open
        currentSession.state.open = currentSession.state.open || this.props.open;
        this.setState(currentSession.state, () => this.regenerateSize());
        break;
    }
  }

  saveSession(reset = false) {
    const currentSession = {
      type: 'saveSession',
      state: this.getCurrentState(),
    };

    if (reset) {
      currentSession.state.open = false;
      currentSession.state.sessionId = UUID.generate();
      currentSession.state.status = STATUSES[0];
      currentSession.state.popoutClosed = false;
    }

    // save it in the parent window for future reference
    this.sendToParentWindow(currentSession);

    // save it locally in case we pop out in the future
    LocalSession.save(this.key, currentSession);

    return currentSession;
  }

  getCurrentState() {
    return {
      sessionId: this.state.sessionId || UUID.generate(),
      status: STATUSES.indexOf(this.state.status) < 0 ? STATUSES[0] : this.state.status,
      open: this.state.open,
      popoutOpen: this.state.popoutOpen,
      popoutClosed: this.state.popoutClosed,
      initialDomain: this.state.initialDomain,
    };
  }

  render() {
    return (
      <Fragment>
        <div id="revealer" onClick={() => this.openForm()} className={this.state.open ? 'hide' : null}>
          <WebsiteWidgetPopout
            disabled={this.state.popoutClosed}
            popoutText={this.props.popoutText}
            onPopout={
              () => {
                this.state.popoutOpen = true;
                this.regenerateSize();
              }
            }
            onClose={
              () => {
                this.setState({
                  popoutOpen: false,
                  popoutClosed: true,
                }, () => this.regenerateSize());
              }
            } />
          <img src={this.props.iconUrl} alt="Text Us" />
        </div>
        <WebsiteWidgetForm
          ref={this.websiteWidgetForm}
          widgetId={this.props.widgetId}
          className={this.state.open ? 'opening' : 'hide'}
          status={this.state.status}
          authenticityToken={this.state.authenticityToken}
          sessionId={this.state.sessionId}
          iframeDomain={this.iframeDomain}
          initialDomain={this.state.initialDomain}
          disabled={this.props.form.disabled}
          disabledHTML={this.props.form.disabledHTML}
          emailEnabled={this.props.form.emailEnabled}
          dropDownEnabled={this.props.form.dropDownEnabled}
          dropDownPrompt={this.props.form.dropDownPrompt}
          dropDownOptions={this.props.form.dropDownOptions}
          messageEnabled={this.props.form.messageEnabled}
          disclaimer={this.props.form.disclaimer}
          marketingConsentText={this.props.form.marketingConsentText}
          marketingConsentOptional={this.props.form.marketingConsentOptional}
          headerText={this.props.form.headerText}
          liveChatText={this.props.form.liveChatText}
          textWithUsText={this.props.form.textWithUsText}
          showNumber={this.props.form.showNumber}
          showNumberThankYou={this.props.form.showNumberThankYou}
          number={this.props.number}
          whiteLabel={this.props.form.whiteLabel}
          onClose={() => this.closeForm()}
          onMinimize={() => this.minimizeForm()}
          onStatusChange={(status) => this.changeStatus(status)} />
      </Fragment>
    );
  }

  openForm() {
    if (this.props.openNewTab && Browser.isIframe()) {
      let newUrl = window.location.href;
      newUrl += (window.location.search === '') ? '?o=t' : '&o=t';

      let options = 'menubar=no,toolbar=no,width=';
      options += this.props.form.width+',height='+this.props.form.height;
      return window.open(newUrl, 'Live Chat', options);
    }

    this.setState({open: true}, () => this.regenerateSize());
  }

  closeForm() {
    const newSession = this.saveSession(true);
    this.setState(newSession.state, () => this.regenerateSize());

    if (this.props.openNewTab && !Browser.isIframe()) return window.close();
  }

  minimizeForm() {
    this.setState({open: false}, () => this.regenerateSize());
  }

  changeStatus(status) {
    if (!status) return;

    this.setState({status: status}, () => this.regenerateSize());
  }

  sendToParentWindow(data) {
    if (this.iframeDomain && Browser.isIframe()) window.parent.postMessage(data, this.iframeDomain);
  }

  regenerateSize() {
    this.sendToParentWindow({
      type: 'sizeUpdate',
      width: `${this.getWidth()}px`,
      height: `${this.getHeight()}px`,
    });
    this.saveSession();
  }

  getWidth() {
    if (this.state.open) return this.props.form.width;

    if (this.props.hideOnLoad) return 0;

    return this.props.frameWidth + (this.state.popoutOpen ? 225 : 0);
  }

  getHeight() {
    if (this.state.open) {
      switch(this.state.status) {
        case 'chatting':
          return 470;
        case 'thank_you':
          return 200 + (this.props.form.showNumberThankYou ? 80 : 0);
        default:
          return this.props.form.height;
      }
    }

    if (this.props.hideOnLoad) return 0;

    return this.props.frameHeight;
  }
}

WebsiteWidget.propTypes = {
  widgetId: PropTypes.string,
  wsUrl: PropTypes.string,

  /* initial open state */
  open: PropTypes.bool,

  /* widget defaults */
  frameWidth: PropTypes.number,
  frameHeight: PropTypes.number,
  iconUrl: PropTypes.string,
  popoutText: PropTypes.string,
  number: PropTypes.string,
  hideOnLoad: PropTypes.bool,
  openNewTab: PropTypes.bool,

  form: PropTypes.exact({
    width: PropTypes.number,
    height: PropTypes.number,
    disabled: PropTypes.bool,
    disabledHTML: PropTypes.string,
    authenticityToken: PropTypes.string,
    emailEnabled: PropTypes.bool,
    dropDownEnabled: PropTypes.bool,
    dropDownPrompt: PropTypes.string,
    dropDownOptions: PropTypes.array,
    messageEnabled: PropTypes.bool,
    disclaimer: PropTypes.string,
    marketingConsentText: PropTypes.string,
    marketingConsentOptional: PropTypes.bool,
    headerText: PropTypes.string,
    liveChatText: PropTypes.string,
    textWithUsText: PropTypes.string,
    showNumber: PropTypes.bool,
    showNumberThankYou: PropTypes.bool,
    whiteLabel: PropTypes.bool,
  }),
};

export default WebsiteWidget;
